import constants from "./constants";
import { TileCardDetails } from "../page/Interfaces";
import { formatCityName, getCityData } from "../config/cityData";
import { deriveVenueSports, sortSports } from "./sport";
import { getVisiblePitchesInCanonicalOrder } from "./turfVisibility";

/**
 * Transforms raw database turf data into TileCardDetails format
 * @param data - Raw turf data from GraphQL API
 * @returns Transformed TileCardDetails object
 */
export const transformTurfData = (data: any, cityNameOverride?: string): TileCardDetails => {
  const visiblePitches = getVisiblePitchesInCanonicalOrder<any>(data?.Pitch);
  const resolvedCityName =
    cityNameOverride ||
    getCityData(data.city)?.name ||
    formatCityName(data.city || "");

  const to12Hr = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    return `${h % 12 || 12}:${m.toString().padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
  };

  const placeholder = "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/placeholder-turf.jpg";

  const getWorkingHours = () => {
    const h = data.workingHours?.find((w: any) => w.dayOfTheWeek === "MONDAY") || data.workingHours?.[0];
    return {
      openingTime: to12Hr(h?.openingTime || "06:00"),
      closingTime: to12Hr(h?.closingTime || "00:00"),
      isAvailable24Hours: !!data.opens24x7,
    };
  };
  // Transform pitches from API format to static format
  const transformPitches = (pitches: any[]) => {
    if (!pitches || !Array.isArray(pitches)) return [];
    
    return pitches.map((pitch: any, index: number) => {
      const area = pitch.area || 0;
      const basePrice = pitch.basePrice || 0;
      
      // Use actual pitchName from DB if available, else generate
      const pitchName =
        pitch.pitchName ||
        `${data.username}-p${index + 1}-${pitch.playingFormat || area || pitch.sport?.toLowerCase() || 'pitch'}`;
      
      return {
        area: area ? area.toString() : "",
        playingFormat: pitch.playingFormat || null,
        sport: pitch.sport || "CRICKET",
        supportedSports: pitch.supportedSports || [pitch.sport || "CRICKET"],
        pitchDimensions: {
          length: pitch.length || null,
          width: pitch.width || null,
          height: pitch.height || null,
        },
        price: {
          weekday: {
            regularHours: basePrice
          },
          weekend: {
            regularHours: basePrice
          }
        },
        pitchName: pitchName,
      };
    });
  };

  // Build imageUrl from sportImages - show all images from all sports
  let imageUrl: string[] = [];
  let sportImagesObj: Record<string, string[]> = {};
  if (data.sportImages) {
    try {
      sportImagesObj =
        typeof data.sportImages === 'string'
          ? JSON.parse(data.sportImages)
          : data.sportImages;
      imageUrl = Object.values(sportImagesObj).flat();
    } catch {
      imageUrl = [];
    }
  }
  if (imageUrl.length === 0) {
    imageUrl = Array.isArray(data.images) ? data.images : [data.images || ""];
  }

  return {
    name: data.name || "",
    userName: data.username || "",
    imageUrl,
    logo: data.logo || "",
    sportImages: sportImagesObj,
    supportedSports: deriveVenueSports({ pitches: visiblePitches }),
    fields: [
      { label: "Contact", key: "actualContact", type: "link" },
      { label: "Contact", key: "displayContact", type: "link" },
      { label: "Partnership", key: "partnership", type: "boolean" },
      { label: "Area & Price", key: "areaPrice", type: "object" },
      { label: "Location", key: "location", type: "float" },
      { label: "Availability Time", key: "availabilityData", type: "object" },
      { label: "Rain Protected", key: "isRainProtected", type: "string" },
      { label: "Cafe", key: "hasCafe", type: "boolean" },
      { label: "Address", key: "address", type: "string" },
    ],
    data: {
      city: resolvedCityName,
      address: data.address || "",
      actualContact: data.supportNumber || "",
      displayContact: constants.BUISNESS_NUMBER,
      partnership: !!data.isPartner,
      isTurfUnavailable: !!data.deletedTurf,
      isTurfHidden: !!data.hideTurf,
      isRainProtected: !!data.isCovered,
      hasCafe: !!data.hasCafe,
      starRating: data.googleRating?.toString() || "0",
      enabledSports: sortSports(data.supportedSports || []),
      availabilityData: getWorkingHours(),
      location: {
        latitude: data.latitude || "",
        longitude: data.longitude || "",
      },
      areaPrice: transformPitches(visiblePitches),
      areaName: data.areaName || "",
      areaId: data.areaId || "",
    },
  };
}; 